import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  ExternalLink,
  FileCode,
  Clock,
  GitBranch,
  Hash,
  Shield,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Wand2,
  Brain,
  CheckCircle2,
  XCircle,
  Bot,
  RefreshCw,
  GitFork,
} from 'lucide-react';
import { Spinner } from '../design-system';
import toast from '../design-system/toast';
import {
  Finding,
  FindingOccurrence,
  FindingOccurrenceListResponse,
  FindingStatus,
} from '../types/finding';
import { FixResponse, FixListResponse } from '../types/fix';
import { ApplicationResponse } from '../types/application';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { useFindingEvidence } from '../hooks/useFindingEvidence';
import { useFindingStatusUpdate } from '../hooks/useFindingStatusUpdate';
import { readNavigationOrigin, useNavigationOrigin } from '../hooks/useNavigationOrigin';
import { apiEndpoints } from '../utils/api';
import { useOrganization } from '../contexts/OrganizationContext';
import FindingCodeSnippet from '../components/findings/FindingCodeSnippet';
import FindingSeverityBadge from '../components/findings/FindingSeverityBadge';
import FindingStatusBadge from '../components/findings/FindingStatusBadge';
import FindingStatusReasonModal from '../components/findings/FindingStatusReasonModal';
import FindingStatusReasonNote from '../components/findings/FindingStatusReasonNote';
import CVSSScoreBadge from '../components/findings/CVSSScoreBadge';
import PopoverPortal from '../components/common/PopoverPortal';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { formatOwaspLabel } from '../utils/owasp';
import { getFindingTitle, isDerivedFindingTitle } from '../utils/findingUtils';

// CVSS metric value labels for human-readable display
const cvssMetricLabels: Record<string, Record<string, string>> = {
  AV: { N: 'Network', A: 'Adjacent', L: 'Local', P: 'Physical' },
  AC: { L: 'Low', H: 'High' },
  PR: { N: 'None', L: 'Low', H: 'High' },
  UI: { N: 'None', R: 'Required' },
  S: { U: 'Unchanged', C: 'Changed' },
  C: { N: 'None', L: 'Low', H: 'High' },
  I: { N: 'None', L: 'Low', H: 'High' },
  A: { N: 'None', L: 'Low', H: 'High' },
};

const getCvssMetricLabel = (metric: string, value?: string): string => {
  if (!value) return 'N/A';
  return cvssMetricLabels[metric]?.[value] || value;
};

const FindingDetailPage: React.FC = () => {
  const { findingId, applicationId: routeApplicationId } = useParams<{ findingId: string; applicationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useAuthenticatedApi();
  const { canWrite } = useOrganization();

  // Where this finding was opened from — a fix, or (by default) the findings list with
  // whatever filters were active.
  const origin = readNavigationOrigin(location.state);
  const backTo =
    origin ?? { to: `/repositories/${routeApplicationId}/findings${location.search}`, label: 'Findings' };
  const handleBackClick = () => navigate(backTo.to);
  // A fix opened from here comes back here, not to the findings list.
  const findingOrigin = useNavigationOrigin('finding');
  const { evidence } = useFindingEvidence(findingId);

  const [finding, setFinding] = useState<Finding | null>(null);
  const [occurrences, setOccurrences] = useState<FindingOccurrence[]>([]);
  const [application, setApplication] = useState<ApplicationResponse | null>(null);
  const [linkedFix, setLinkedFix] = useState<FixResponse | null>(null);
  const [isLoadingLinkedFix, setIsLoadingLinkedFix] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const { updateStatus: changeStatus, updatingFindingId, reasonPrompt } = useFindingStatusUpdate(setFinding);
  const isUpdatingStatus = updatingFindingId !== null;
  const [isCreatingFix, setIsCreatingFix] = useState(false);
  const [isRetryingFix, setIsRetryingFix] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const [showCvssDetails, setShowCvssDetails] = useState(false);
  const [showKolegaDetails, setShowKolegaDetails] = useState(false);


  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  useEffect(() => {
    if (findingId) {
      fetchFinding();
      fetchOccurrences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findingId]);

  // Fetch linked fix when finding is loaded
  useEffect(() => {
    if (finding) {
      setIsLoadingLinkedFix(true);
      fetchLinkedFix();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finding?.id, finding?.organization_id]);

  // Poll for fix status updates when fix is running or pending
  useEffect(() => {
    if (!linkedFix || (linkedFix.status !== 'running' && linkedFix.status !== 'pending')) {
      return;
    }

    const pollInterval = setInterval(() => {
      fetchLinkedFix();
    }, 5000); // Poll every 5 seconds

    // Stop polling after 10 minutes
    const timeout = setTimeout(() => clearInterval(pollInterval), 600000);

    return () => {
      clearInterval(pollInterval);
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedFix?.id, linkedFix?.status]);

  const fetchFinding = async () => {
    setIsLoading(true);
    try {
      const response = await api.get<Finding>(apiEndpoints.findings.get(findingId!));
      setFinding(response);

      // Fetch application data
      try {
        const appResponse = await api.get<ApplicationResponse>(
          apiEndpoints.applications.get(response.organization_id, response.application_id)
        );
        setApplication(appResponse);
      } catch {
        // Silently fail if application fetch fails
      }
    } catch (error: any) {
      console.error('Error fetching finding:', error);
      toast.error('Failed to load finding');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOccurrences = async () => {
    try {
      const response = await api.get<FindingOccurrenceListResponse>(
        apiEndpoints.findings.occurrences(findingId!)
      );
      setOccurrences(response.occurrences);
    } catch (error: any) {
      console.error('Error fetching occurrences:', error);
    }
  };

  const fetchLinkedFix = async () => {
    if (!finding) return;
    try {
      const response = await api.get<FixListResponse>(
        apiEndpoints.fixes.listByFinding(finding.organization_id, finding.id)
      );
      // Get the most recent fix (first in list since sorted by created_at desc)
      if (response.fixes.length > 0) {
        setLinkedFix(response.fixes[0]);
      } else {
        setLinkedFix(null);
      }
    } catch (error: any) {
      console.error('Error fetching linked fix:', error);
      setLinkedFix(null);
    } finally {
      setIsLoadingLinkedFix(false);
    }
  };

  const updateStatus = (newStatus: FindingStatus) => {
    if (!finding) return;

    // Close the menu first: it is portaled above the reason dialog.
    setShowStatusDropdown(false);
    changeStatus(finding, newStatus);
  };

  const handleAutofix = async () => {
    if (!finding) return;

    setIsCreatingFix(true);
    try {
      if (finding.status === 'needs_manual_review') {
        const updatedFinding = await api.patch<Finding>(
          apiEndpoints.findings.updateStatus(findingId!),
          { status: 'open' }
        );
        setFinding(updatedFinding);
      }

      const response = await api.post<FixResponse>(
        apiEndpoints.findings.autofix(findingId!),
        {}
      );
      toast.success('Fix created! Redirecting...');
      navigate(`/repositories/${routeApplicationId}/fixes/${response.id}`, { state: findingOrigin });
    } catch (error: any) {
      console.error('Error creating autofix:', error);
      toast.error(error.message || 'Failed to create fix');
    } finally {
      setIsCreatingFix(false);
    }
  };

  const handleRetryFix = async () => {
    if (!linkedFix || !finding) return;

    setIsRetryingFix(true);
    try {
      const response = await api.post<FixResponse>(
        apiEndpoints.fixes.retry(finding.organization_id, linkedFix.id),
        {}
      );
      setLinkedFix(response);
      toast.success('Fix retrying...');
    } catch (error: any) {
      console.error('Error retrying fix:', error);
      toast.error(error.message || 'Failed to retry fix');
    } finally {
      setIsRetryingFix(false);
    }
  };

  const getAssessmentStatusColor = (status?: string) => {
    switch (status) {
      case 'completed':
        return 'text-semantic-success';
      case 'failed':
        return 'text-semantic-error';
      case 'in_progress':
        return 'text-semantic-warning';
      default:
        return 'text-ui-text-tertiary';
    }
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size={32} />
      </div>
    );
  }

  if (!finding) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-ui-text-secondary">Finding not found</p>
      </div>
    );
  }

  const statusOptions: { value: FindingStatus; label: string }[] = [
    { value: 'open', label: 'Open' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'ignored', label: 'Ignored' },
    { value: 'false_positive', label: 'False Positive' },
    { value: 'needs_manual_review', label: 'Needs Review' },
  ];
  const detailBranch = finding.branch || occurrences.find((occurrence) => occurrence.branch)?.branch;
  const canAutofixFinding = finding.status === 'open' || finding.status === 'needs_manual_review';

  return (
    <div className="min-h-screen bg-ui-background-primary">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Back Button */}
        <button
          onClick={handleBackClick}
          className="flex items-center gap-2 text-sm text-ui-text-secondary hover:text-ui-text-primary mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {backTo.label}
        </button>

        {/* Compact Header Card */}
        <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4 sm:p-6 mb-6">
          {/* Title row with actions */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-2">
            <h1
              className={`text-2xl font-bold text-ui-text-primary min-w-0 break-words${
                isDerivedFindingTitle(finding) ? ' capitalize' : ''
              }`}
            >
              {getFindingTitle(finding)}
            </h1>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
              {/* Autofix Button — branches: loading / retry / view linked fix / new autofix.
                  The view-linked-fix branch is read-only navigation; the retry and autofix
                  branches are mutations that disable for non-writers (viewers / demo).
                  Needs Review findings are auto-opened before fix creation, matching
                  the queue modal behavior. */}
              {isLoadingLinkedFix ? (
                <button
                  disabled
                  className="flex flex-1 sm:flex-none items-center justify-center gap-2 px-4 py-2 bg-ui-background-tertiary text-ui-text-secondary font-medium rounded-lg"
                >
                  <Spinner size={16} tone="current" />
                  Loading...
                </button>
              ) : linkedFix?.status === 'failed' ? (
                <button
                  onClick={handleRetryFix}
                  disabled={isRetryingFix || !canWrite}
                  className="flex flex-1 sm:flex-none items-center justify-center gap-2 px-4 py-2 bg-brand-primary hover:bg-brand-primaryDark text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRetryingFix ? <Spinner size={16} tone="current" /> : <RefreshCw className="w-4 h-4" />}
                  Retry Fix
                </button>
              ) : linkedFix ? (
                <button
                  onClick={() =>
                    navigate(`/repositories/${routeApplicationId}/fixes/${linkedFix.id}`, {
                      state: findingOrigin,
                    })
                  }
                  className={`flex flex-1 sm:flex-none items-center justify-center gap-2 px-4 py-2 font-medium rounded-lg transition-colors ${
                    linkedFix.status === 'running'
                      ? 'bg-blue-500 hover:bg-blue-600 text-white'
                      : linkedFix.status === 'completed'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                      : 'bg-gray-600 hover:bg-gray-700 text-white'
                  }`}
                >
                  {linkedFix.status === 'running' ? (
                    <Spinner size={16} tone="current" />
                  ) : linkedFix.status === 'completed' ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <Wand2 className="w-4 h-4" />
                  )}
                  {linkedFix.status === 'running'
                    ? 'Fix Running...'
                    : linkedFix.status === 'completed'
                    ? 'View Fix'
                    : 'Fix Pending'}
                  {linkedFix.status === 'completed' && (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
              ) : (
                <button
                  onClick={handleAutofix}
                  disabled={isCreatingFix || !canAutofixFinding || !canWrite}
                  className="flex flex-1 sm:flex-none items-center justify-center gap-2 px-4 py-2 bg-brand-primary hover:bg-brand-primaryDark text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingFix ? (
                    <Spinner size={16} tone="current" />
                  ) : (
                    <Wand2 className="w-4 h-4" />
                  )}
                  Propose Fix
                </button>
              )}

              {/* Status Update Dropdown — trigger disabled for non-writers so the greyed
                  button signals "there is a status control here, you just can't use it". */}
              <div className="relative flex-1 sm:flex-none" ref={statusDropdownRef}>
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  disabled={isUpdatingStatus || !canWrite}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-ui-background-tertiary hover:bg-ui-background-primary border border-ui-border-primary text-ui-text-primary rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-ui-background-tertiary"
                >
                  {isUpdatingStatus ? (
                    <Spinner size={16} tone="current" />
                  ) : (
                    <>
                      Update Status
                      <ChevronDown className="w-4 h-4" />
                    </>
                  )}
                </button>

                <PopoverPortal
                  anchorRef={statusDropdownRef}
                  open={showStatusDropdown && canWrite}
                  onClose={() => setShowStatusDropdown(false)}
                  align="end"
                  width={192}
                  className="bg-ui-background-secondary border border-ui-border-primary rounded-lg shadow-lg overflow-hidden"
                >
                    {statusOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => updateStatus(option.value)}
                        disabled={finding.status === option.value}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-ui-background-tertiary transition-colors first:rounded-t-lg last:rounded-b-lg ${
                          finding.status === option.value
                            ? 'text-ui-text-tertiary cursor-not-allowed'
                            : 'text-ui-text-primary'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                </PopoverPortal>
              </div>
            </div>
          </div>

          {/* Inline Metadata Strip */}
          <div className="space-y-2 mb-2">
            <div className="flex items-center gap-x-3 gap-y-2 flex-wrap">
              <div className="relative group flex items-center gap-1.5 cursor-help">
                <span className="text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">Kolega Severity</span>
                <FindingSeverityBadge severity={finding.severity} />
                {/* Hover tooltip */}
                <div className="hidden sm:block absolute left-0 top-full mt-2 w-80 bg-ui-background-secondary border border-ui-border-primary rounded-lg shadow-lg p-4 z-20 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150">
                  <p className="text-xs text-ui-text-secondary mb-2">
                    Kolega Severity is assigned by our AI agent after analysing the code context. It considers factors such as:
                  </p>
                  <ul className="text-xs text-ui-text-tertiary space-y-1 list-disc pl-4">
                    <li>Practical exploitability and attack complexity</li>
                    <li>Whether the finding is a real concern or false positive</li>
                    <li>Code context and compensating controls</li>
                    <li>Repository type and deployment exposure</li>
                    <li>Data sensitivity and authentication model</li>
                  </ul>
                </div>
              </div>
              {finding.cvss_base_score !== undefined && finding.cvss_base_score !== null && (
                <div className="relative group/cvss flex items-center gap-1.5 cursor-help">
                  <span className="text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">CVSS</span>
                  <CVSSScoreBadge score={finding.cvss_base_score} severity={finding.cvss_severity} compact />
                  <div className="hidden sm:block absolute left-0 top-full mt-2 w-72 bg-ui-background-secondary border border-ui-border-primary rounded-lg shadow-lg p-4 z-20 opacity-0 invisible group-hover/cvss:opacity-100 group-hover/cvss:visible transition-all duration-150">
                    <p className="text-xs text-ui-text-secondary mb-2">
                      CVSS 3.1 (Common Vulnerability Scoring System) is an industry-standard score from 0.0 to 10.0.
                    </p>
                    <p className="text-xs text-ui-text-tertiary">
                      Calculated from 8 base metrics covering exploitability (attack vector, complexity, privileges, user interaction) and impact (confidentiality, integrity, availability).
                    </p>
                  </div>
                </div>
              )}
              <div className="relative group/status flex items-center gap-1.5 cursor-help">
                <span className="text-xs font-medium text-ui-text-tertiary uppercase tracking-wider">Status</span>
                <FindingStatusBadge status={finding.status} />
                <div className="hidden sm:block absolute left-0 top-full mt-2 w-64 bg-ui-background-secondary border border-ui-border-primary rounded-lg shadow-lg p-4 z-20 opacity-0 invisible group-hover/status:opacity-100 group-hover/status:visible transition-all duration-150">
                  <p className="text-xs text-ui-text-secondary mb-2">
                    The current triage status of this finding.
                  </p>
                  <ul className="text-xs text-ui-text-tertiary space-y-1">
                    <li><span className="text-ui-text-secondary font-medium">Open</span> — requires attention</li>
                    <li><span className="text-ui-text-secondary font-medium">Resolved</span> — has been fixed</li>
                    <li><span className="text-ui-text-secondary font-medium">Ignored</span> — acknowledged, not fixing</li>
                    <li><span className="text-ui-text-secondary font-medium">False Positive</span> — not a real vulnerability</li>
                    <li><span className="text-ui-text-secondary font-medium">Needs Review</span> — requires manual assessment</li>
                  </ul>
                </div>
              </div>
              {/* A person's reason supersedes the AI chip: they made the latest call. */}
              {finding.dismissed_by === 'ai' && !finding.status_reason && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20">
                  <Bot className="w-3.5 h-3.5" />
                  AI Dismissed
                </span>
              )}
            </div>

            {finding.status_reason && <FindingStatusReasonNote statusReason={finding.status_reason} />}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 min-w-0">
              {finding.repository_full_name && (
                <div className="flex items-center gap-1.5 min-w-0 w-full sm:w-auto">
                  <span className="text-xs font-medium text-ui-text-tertiary uppercase tracking-wider flex-shrink-0">Repo</span>
                  <GitFork className="w-3.5 h-3.5 text-ui-text-secondary flex-shrink-0" />
                  <span className="text-sm text-ui-text-secondary font-mono truncate min-w-0 max-w-full sm:max-w-[260px]" title={finding.repository_full_name}>
                    {finding.repository_full_name}
                  </span>
                </div>
              )}
              {detailBranch && (
                <div className="flex items-center gap-1.5 min-w-0 w-full sm:w-auto">
                  <span className="text-xs font-medium text-ui-text-tertiary uppercase tracking-wider flex-shrink-0">Branch</span>
                  <GitBranch className="w-3.5 h-3.5 text-ui-text-secondary flex-shrink-0" />
                  <span className="text-sm text-ui-text-secondary font-mono truncate min-w-0 max-w-full sm:max-w-[220px]" title={detailBranch}>
                    {detailBranch}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-1.5 min-w-0 w-full sm:w-auto">
                <span className="text-xs font-medium text-ui-text-tertiary uppercase tracking-wider flex-shrink-0">Location</span>
                <span className="text-sm text-ui-text-secondary font-mono truncate min-w-0 max-w-full sm:max-w-[360px]" title={finding.file_path}>
                  {finding.file_path}
                </span>
              </div>
            </div>
          </div>

          {/* App link */}
          <div className="flex items-center gap-2 text-sm mb-3 flex-wrap">
            <button
              onClick={() => navigate(`/repositories/${finding.application_id}`)}
              className="text-brand-primary hover:text-brand-primaryDark flex items-center gap-1 transition-colors"
            >
              {application?.name || 'Loading...'}
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Description - full width */}
          <p className="text-sm text-ui-text-secondary">{finding.message}</p>
        </div>

        {/* The code the finding points at */}
        {evidence && <FindingCodeSnippet evidence={evidence} />}

        {/* Confirmed-real confidence banner */}
        {finding.assessment_status === 'completed' &&
          (finding.is_real_concern === 'Yes' || finding.is_real_concern === 'Yes, but low impact') && (
            <div className="flex items-center gap-3 bg-semantic-success/10 border border-semantic-success/20 rounded-xl px-4 py-3.5 mb-6">
              <CheckCircle2 className="w-5 h-5 text-semantic-success flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-semantic-success">
                  Confirmed real{finding.is_real_concern === 'Yes, but low impact' ? ' — low impact' : ''}
                </div>
                <div className="text-xs text-ui-text-secondary mt-0.5">
                  Kolega's AI validated this against the surrounding code; it is not a false positive.
                </div>
              </div>
            </div>
          )}

        {/* AI Analysis */}
        <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4 sm:p-6 mb-6">
          <h2 className="text-sm font-semibold text-brand-primary flex items-center gap-2 pb-3 border-b border-ui-border-primary">
            <Brain className="w-4 h-4" />
            AI Analysis
            {finding.assessment_status && finding.assessment_status !== 'completed' && (
              <span className={`text-xs font-normal ml-1 ${getAssessmentStatusColor(finding.assessment_status)}`}>
                ({finding.assessment_status === 'in_progress' ? 'Analyzing...' : finding.assessment_status})
              </span>
            )}
            {finding.assessed_at && finding.assessment_status === 'completed' && (
              <span className="text-xs font-normal text-ui-text-tertiary ml-1">
                · Assessed {formatDateTime(finding.assessed_at)}
              </span>
            )}
          </h2>

          <div className="mt-4">
            {finding.assessment_status === 'completed' ? (
              <div>
                {finding.assessment_notes ? (
                  <div className="p-4 bg-ui-background-tertiary rounded-lg text-ui-text-primary text-sm [&_h1]:text-ui-text-primary [&_h2]:text-ui-text-primary [&_h3]:text-ui-text-primary [&_h4]:text-ui-text-primary [&_p]:text-ui-text-primary [&_li]:text-ui-text-primary [&_strong]:text-ui-text-primary [&_em]:text-ui-text-secondary [&_code]:text-brand-primary [&_code]:bg-ui-background-secondary [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_pre]:bg-ui-background-secondary [&_pre]:p-3 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_a]:text-brand-primary [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:border-l-2 [&_blockquote]:border-ui-border-primary [&_blockquote]:pl-4 [&_blockquote]:text-ui-text-secondary space-y-3">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {finding.assessment_notes}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-sm text-ui-text-secondary">No analysis notes available.</p>
                )}
              </div>
            ) : finding.assessment_status === 'in_progress' ? (
              <div className="flex items-center gap-3 p-4 bg-ui-background-tertiary rounded-lg">
                <Spinner size={20} tone="current" className="text-semantic-warning" />
                <div>
                  <p className="text-sm font-medium text-ui-text-primary">Assessment in Progress</p>
                  <p className="text-xs text-ui-text-secondary">
                    AI is analyzing the code context to determine if this is a real vulnerability...
                  </p>
                </div>
              </div>
            ) : finding.assessment_status === 'failed' ? (
              <div className="flex items-center gap-3 p-4 bg-semantic-error/10 border border-semantic-error/20 rounded-lg">
                <XCircle className="w-5 h-5 text-semantic-error" />
                <div>
                  <p className="text-sm font-medium text-ui-text-primary">Assessment Failed</p>
                  <p className="text-xs text-ui-text-secondary">
                    {finding.assessment_notes || 'An error occurred during assessment. You can try again.'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <Brain className="w-12 h-12 text-ui-text-tertiary mx-auto mb-3" />
                <p className="text-sm text-ui-text-secondary">
                  AI assessment pending. Findings are automatically assessed after each scan.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Severity Scores */}
        <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4 sm:p-6 mb-6">
          <h2 className="text-sm font-semibold text-ui-text-primary mb-4 flex items-center gap-2 pb-3 border-b border-ui-border-primary">
            <Shield className="w-4 h-4" />
            Severity Scores
          </h2>

          {/* Kolega Severity */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium text-ui-text-secondary uppercase tracking-wider">Kolega Severity</span>
              <FindingSeverityBadge severity={finding.severity} />
            </div>
            {finding.assessment_status === 'completed' && (
              <div>
                <button
                  onClick={() => setShowKolegaDetails(!showKolegaDetails)}
                  className="flex items-center gap-1 text-xs text-ui-text-secondary hover:text-ui-text-primary transition-colors mt-2"
                >
                  {showKolegaDetails ? 'Hide Assessment Factors' : 'Show Assessment Factors'}
                  {showKolegaDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                {showKolegaDetails && (
                  <div className="mt-3">
                    <p className="text-xs text-ui-text-tertiary mb-3">
                      Determined by our AI agent after analysing code context, exploitability, and real-world risk.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div className="p-3 bg-ui-background-tertiary rounded-lg">
                        <p className="text-xs text-ui-text-tertiary mb-0.5">Priority</p>
                        <p className={`text-sm font-medium ${
                          finding.assessed_priority === 'High' ? 'text-semantic-error' :
                          finding.assessed_priority === 'Medium' ? 'text-semantic-warning' :
                          'text-semantic-success'
                        }`}>
                          {finding.assessed_priority || 'N/A'}
                        </p>
                      </div>
                      <div className="p-3 bg-ui-background-tertiary rounded-lg">
                        <p className="text-xs text-ui-text-tertiary mb-0.5">Exploitability</p>
                        <p className={`text-sm font-medium ${
                          finding.practical_exploitability === 'High' ? 'text-semantic-error' :
                          finding.practical_exploitability === 'Medium' ? 'text-semantic-warning' :
                          'text-semantic-success'
                        }`}>
                          {finding.practical_exploitability || 'N/A'}
                        </p>
                      </div>
                      <div className="p-3 bg-ui-background-tertiary rounded-lg">
                        <p className="text-xs text-ui-text-tertiary mb-0.5">Real Concern</p>
                        <p className="text-sm font-medium text-ui-text-primary">{finding.is_real_concern || 'N/A'}</p>
                      </div>
                      <div className="p-3 bg-ui-background-tertiary rounded-lg">
                        <p className="text-xs text-ui-text-tertiary mb-0.5">Should Fix</p>
                        <p className="text-sm font-medium text-ui-text-primary">{finding.should_fix || 'N/A'}</p>
                      </div>
                      <div className="p-3 bg-ui-background-tertiary rounded-lg">
                        <p className="text-xs text-ui-text-tertiary mb-0.5">Code at Fault</p>
                        <p className="text-sm font-medium text-ui-text-primary">{finding.is_code_at_fault || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* CVSS 3.1 */}
          {finding.cvss_base_score !== undefined && finding.cvss_base_score !== null && (
            <div className="pt-4 border-t border-ui-border-primary">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-ui-text-secondary uppercase tracking-wider">CVSS 3.1</span>
                <CVSSScoreBadge score={finding.cvss_base_score} severity={finding.cvss_severity} compact />
              </div>
              <button
                onClick={() => setShowCvssDetails(!showCvssDetails)}
                aria-expanded={showCvssDetails}
                className="flex items-center gap-1 text-xs text-ui-text-secondary hover:text-ui-text-primary transition-colors mt-2"
              >
                {showCvssDetails ? 'Hide CVSS Details' : 'Show CVSS Details'}
                {showCvssDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              {showCvssDetails && (
                <div className="mt-3">
                  {finding.cvss_vector && (
                    <code className="inline-block text-xs text-ui-text-secondary font-mono bg-ui-background-tertiary px-2.5 py-1 rounded mb-3 break-all">
                      {finding.cvss_vector}
                    </code>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-3 bg-ui-background-tertiary rounded-lg">
                      <p className="text-xs text-ui-text-tertiary mb-0.5">Attack Vector</p>
                      <p className="text-sm text-ui-text-primary">{getCvssMetricLabel('AV', finding.cvss_attack_vector)}</p>
                    </div>
                    <div className="p-3 bg-ui-background-tertiary rounded-lg">
                      <p className="text-xs text-ui-text-tertiary mb-0.5">Attack Complexity</p>
                      <p className="text-sm text-ui-text-primary">{getCvssMetricLabel('AC', finding.cvss_attack_complexity)}</p>
                    </div>
                    <div className="p-3 bg-ui-background-tertiary rounded-lg">
                      <p className="text-xs text-ui-text-tertiary mb-0.5">Privileges Required</p>
                      <p className="text-sm text-ui-text-primary">{getCvssMetricLabel('PR', finding.cvss_privileges_required)}</p>
                    </div>
                    <div className="p-3 bg-ui-background-tertiary rounded-lg">
                      <p className="text-xs text-ui-text-tertiary mb-0.5">User Interaction</p>
                      <p className="text-sm text-ui-text-primary">{getCvssMetricLabel('UI', finding.cvss_user_interaction)}</p>
                    </div>
                    <div className="p-3 bg-ui-background-tertiary rounded-lg">
                      <p className="text-xs text-ui-text-tertiary mb-0.5">Scope</p>
                      <p className="text-sm text-ui-text-primary">{getCvssMetricLabel('S', finding.cvss_scope)}</p>
                    </div>
                    <div className="p-3 bg-ui-background-tertiary rounded-lg">
                      <p className="text-xs text-ui-text-tertiary mb-0.5">Confidentiality</p>
                      <p className="text-sm text-ui-text-primary">{getCvssMetricLabel('C', finding.cvss_confidentiality)}</p>
                    </div>
                    <div className="p-3 bg-ui-background-tertiary rounded-lg">
                      <p className="text-xs text-ui-text-tertiary mb-0.5">Integrity</p>
                      <p className="text-sm text-ui-text-primary">{getCvssMetricLabel('I', finding.cvss_integrity)}</p>
                    </div>
                    <div className="p-3 bg-ui-background-tertiary rounded-lg">
                      <p className="text-xs text-ui-text-tertiary mb-0.5">Availability</p>
                      <p className="text-sm text-ui-text-primary">{getCvssMetricLabel('A', finding.cvss_availability)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Compliance & References */}
        {(finding.cwe.length > 0 || finding.owasp.length > 0 || finding.vulnerability_class.length > 0 || finding.source_url || finding.references.length > 0) && (
          <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4 sm:p-6 mb-6">
            <h2 className="text-sm font-semibold text-ui-text-primary mb-4 flex items-center gap-2 pb-3 border-b border-ui-border-primary">
              <Shield className="w-4 h-4" />
              Compliance & References
            </h2>
            <div className="flex items-center flex-wrap gap-x-4 gap-y-2">
              {finding.cwe.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-ui-text-secondary uppercase tracking-wider">CWE</span>
                  <div className="flex flex-wrap gap-1.5">
                    {finding.cwe.map((cwe, index) => (
                      <a
                        key={index}
                        href={`https://cwe.mitre.org/data/definitions/${cwe.match(/\d+/)?.[0]}.html`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-ui-background-tertiary text-ui-text-primary rounded text-xs hover:bg-ui-background-primary transition-colors"
                      >
                        {cwe.split(':')[0]}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {finding.cwe.length > 0 && (finding.vulnerability_class.length > 0 || finding.owasp.length > 0) && (
                <span className="text-ui-text-tertiary">·</span>
              )}

              {finding.vulnerability_class.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-ui-text-secondary uppercase tracking-wider">Class</span>
                  <div className="flex flex-wrap gap-1.5">
                    {finding.vulnerability_class.map((vc, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-0.5 bg-ui-background-tertiary text-ui-text-primary rounded text-xs"
                      >
                        {vc.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {(finding.cwe.length > 0 || finding.vulnerability_class.length > 0) && finding.owasp.length > 0 && (
                <span className="text-ui-text-tertiary">·</span>
              )}

              {finding.owasp.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-ui-text-secondary uppercase tracking-wider">OWASP</span>
                  <div className="flex flex-wrap gap-1.5">
                    {finding.owasp.map((owasp, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-0.5 bg-ui-background-tertiary text-ui-text-primary rounded text-xs"
                      >
                        {formatOwaspLabel(owasp)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {(finding.source_url || finding.references.length > 0) && (
                <>
                  <span className="text-ui-text-tertiary">·</span>
                  <div className="flex items-center gap-2">
                    {finding.source_url && (
                      <a
                        href={finding.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-brand-primary hover:text-brand-primaryDark transition-colors"
                      >
                        Rule Docs
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {finding.references.map((ref, index) => (
                      <a
                        key={index}
                        href={ref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-ui-text-secondary hover:text-ui-text-primary transition-colors"
                      >
                        Ref {index + 1}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Detection History */}
        <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl p-4 sm:p-6">
          <h2 className="text-sm font-semibold text-ui-text-primary mb-4 flex items-center gap-2 pb-3 border-b border-ui-border-primary">
            <Hash className="w-4 h-4" />
            Detection History
            <span className="inline-flex items-center justify-center px-2 py-0.5 bg-ui-background-tertiary text-ui-text-secondary rounded-full text-xs font-medium">
              {occurrences.length}
            </span>
          </h2>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ui-text-secondary mb-4">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              <span>First detected {formatDateTime(finding.first_detected_at)}</span>
            </div>
            <span className="hidden sm:inline text-ui-text-tertiary">·</span>
            <span>Last detected {formatDateTime(finding.last_detected_at)}</span>
            {finding.resolved_at && (
              <>
                <span className="hidden sm:inline text-ui-text-tertiary">·</span>
                <span>Resolved {formatDateTime(finding.resolved_at)}</span>
              </>
            )}
          </div>

          {occurrences.length > 0 ? (
            <div className="space-y-3">
              {occurrences.map((occurrence) => (
                <div
                  key={occurrence.id}
                  className="p-4 bg-ui-background-tertiary rounded-lg space-y-2"
                >
                  <div className="flex items-center gap-2 text-sm text-ui-text-primary font-mono">
                    <FileCode className="w-4 h-4 text-ui-text-secondary flex-shrink-0" />
                    <span className="break-all">
                      {finding.file_path}:{occurrence.line_start}-{occurrence.line_end}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ui-text-secondary">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {formatDateTime(occurrence.detected_at)}
                    </div>
                    <div className="flex items-center gap-2">
                      <GitBranch className="w-4 h-4" />
                      {occurrence.branch}
                    </div>
                    {occurrence.commit_hash && (
                      <div className="flex items-center gap-2 font-mono">
                        <Hash className="w-4 h-4" />
                        {occurrence.commit_hash.substring(0, 7)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-ui-text-secondary">No occurrences recorded</p>
          )}
        </div>
      </div>

      <FindingStatusReasonModal {...reasonPrompt} />
    </div>
  );
};

export default FindingDetailPage;
